import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Users,
    Plus,
    Pencil,
    Trash2,
    Send,
    Loader2,
    UserMinus,
    UserPlus,
    Upload,
    ChevronLeft,
    MessageSquare,
    Printer,
    FileDown,
    Image as ImageIcon,
    X,
    IdCard,
    AlertTriangle,
} from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../components/ui/hover-card";
import { toast } from "sonner";
import { BadgePreview } from "../components/BadgePreview";
import { BadgeBack } from "../components/BadgeBack";
import { downloadA4BadgesPDF } from "../lib/pdf";
import { loadSettings, getVerifyBaseUrl } from "../lib/settings";
import { recordPrintEvent } from "../lib/printHistory";

const COLORS = ["#376DB3", "#059669", "#D97706", "#DC2626", "#7C3AED", "#0891B2", "#E11D48", "#65A30D"];

// Recommended minimum photo resolution for professional badge printing
const PHOTO_MIN_W = 800;
const PHOTO_MIN_H = 1000;

// Measure a base64 photo; resolves employee id when below the recommended resolution.
const checkPhotoLowRes = (emp) =>
    new Promise((resolve) => {
        if (!emp.photo) return resolve(null);
        const img = new Image();
        img.onload = () => resolve(img.width < PHOTO_MIN_W || img.height < PHOTO_MIN_H ? emp.id : null);
        img.onerror = () => resolve(null);
        img.src = emp.photo;
    });

// Resize the uploaded client logo to max 600px wide and encode as data URL.
// PNG sources keep transparency; everything else becomes JPEG.
const resizeLogoFile = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => {
            const img = new Image();
            img.onerror = reject;
            img.onload = () => {
                const maxW = 600;
                const scale = Math.min(1, maxW / img.width);
                const w = Math.round(img.width * scale);
                const h = Math.round(img.height * scale);
                const canvas = document.createElement("canvas");
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, w, h);
                resolve(canvas.toDataURL(file.type === "image/png" ? "image/png" : "image/jpeg", 0.85));
            };
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
    });

export default function GroupsPage() {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editGroup, setEditGroup] = useState(null);
    const [selectedGroupId, setSelectedGroupId] = useState(null);

    const fetchGroups = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/groups");
            setGroups(data);
        } catch (e) {
            toast.error("Errore caricamento gruppi");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGroups();
    }, []);

    const handleDelete = async (g) => {
        if (!window.confirm(`Eliminare il gruppo "${g.name}"? I dipendenti non saranno cancellati.`)) return;
        try {
            await api.delete(`/groups/${g.id}`);
            toast.success("Gruppo eliminato");
            fetchGroups();
        } catch (e) {
            toast.error("Errore eliminazione");
        }
    };

    return (
        <div data-testid="groups-page">
            <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
                <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                            <Users className="h-6 w-6" />
                        </div>
                        <div>
                            <h1 className="font-display text-2xl font-bold text-[#376DB3]">Gruppi di dipendenti</h1>
                            <p className="text-sm text-slate-600">
                                Crea gruppi per stampare tesserini selettivi o inviare comunicazioni mirate.
                            </p>
                        </div>
                    </div>
                    <Button
                        onClick={() => setEditGroup({})}
                        className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                        data-testid="new-group-btn"
                    >
                        <Plus className="h-4 w-4" /> Nuovo gruppo
                    </Button>
                </div>

                <div className={`flex items-center justify-center py-16 text-slate-500 ${loading ? "" : "hidden"}`}>
                    <Loader2 className="h-6 w-6 animate-spin" />
                </div>
                <div className={`rounded-lg border border-dashed border-slate-300 bg-white py-16 text-center ${!loading && groups.length === 0 ? "" : "hidden"}`}>
                    <Users className="mx-auto h-10 w-10 text-slate-300" />
                    <div className="mt-3 font-display text-lg font-semibold text-slate-700">Nessun gruppo creato</div>
                    <div className="mt-1 text-sm text-slate-500">Crea il primo gruppo con il bottone in alto a destra.</div>
                </div>
                <div className={`grid gap-4 sm:grid-cols-2 lg:grid-cols-3 ${!loading && groups.length > 0 ? "" : "hidden"}`} data-testid="groups-list">
                    {groups.map((g) => (
                        <GroupCard
                            key={g.id}
                            group={g}
                            onEdit={() => setEditGroup(g)}
                            onDelete={() => handleDelete(g)}
                            onOpen={() => setSelectedGroupId(g.id)}
                        />
                    ))}
                </div>
            </main>

            <GroupEditDialog
                open={!!editGroup}
                group={editGroup}
                onClose={() => setEditGroup(null)}
                onSaved={() => { setEditGroup(null); fetchGroups(); }}
            />
            <GroupDetailDialog
                open={!!selectedGroupId}
                groupId={selectedGroupId}
                initialGroup={groups.find((g) => g.id === selectedGroupId) || null}
                onClose={() => { setSelectedGroupId(null); fetchGroups(); }}
            />
        </div>
    );
}

const GroupCard = ({ group, onEdit, onDelete, onOpen }) => (
    <div
        className="group flex flex-col rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
        data-testid={`group-card-${group.id}`}
    >
        <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
                <div className="h-3 w-3 flex-shrink-0 rounded-full" style={{ backgroundColor: group.color }} />
                <h3 className="font-display font-semibold text-slate-800">{group.name}</h3>
            </div>
            <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                <button
                    onClick={(e) => { e.stopPropagation(); onEdit(); }}
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    data-testid={`group-edit-${group.id}`}
                    title="Modifica"
                >
                    <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                    data-testid={`group-delete-${group.id}`}
                    title="Elimina"
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </button>
            </div>
        </div>
        <p className={`mt-1 line-clamp-2 text-xs text-slate-500 ${group.description ? "" : "hidden"}`}>{group.description || ""}</p>
        <div className="mt-3 flex items-center justify-between">
            <div className="text-sm">
                <span className="font-display text-2xl font-bold text-[#376DB3] font-mono-numbers">{group.member_count}</span>
                <span className="ml-1 text-xs text-slate-500">dipendenti</span>
            </div>
            <div className="flex items-center gap-2">
                {group.cliente_logo && (
                    <img
                        src={group.cliente_logo}
                        alt="Logo cliente"
                        title="Logo cliente del gruppo — stampato sui tesserini dei membri"
                        className="h-7 max-w-[72px] object-contain"
                        data-testid={`group-logo-thumb-${group.id}`}
                    />
                )}
                <Button size="sm" variant="outline" onClick={onOpen} data-testid={`group-open-${group.id}`}>
                    Apri
                </Button>
            </div>
        </div>
    </div>
);

const GroupEditDialog = ({ open, group, onClose, onSaved }) => {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [color, setColor] = useState(COLORS[0]);
    const [clienteLogo, setClienteLogo] = useState(null);
    const [saving, setSaving] = useState(false);
    const logoInputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setName(group?.name || "");
            setDescription(group?.description || "");
            setColor(group?.color || COLORS[0]);
            setClienteLogo(group?.cliente_logo || null);
        }
    }, [open, group]);

    const isEdit = !!group?.id;

    const handleLogoFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Carica un file immagine");
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Immagine troppo grande (max 8 MB)");
            return;
        }
        try {
            const dataUrl = await resizeLogoFile(file);
            setClienteLogo(dataUrl);
        } catch (err) {
            toast.error("Impossibile elaborare il logo");
        }
        e.target.value = "";
    };

    const handleSave = async () => {
        if (!name.trim()) {
            toast.error("Nome richiesto");
            return;
        }
        setSaving(true);
        try {
            const payload = { name, description, color, cliente_logo: clienteLogo };
            if (isEdit) {
                await api.put(`/groups/${group.id}`, payload);
                toast.success("Gruppo aggiornato");
            } else {
                await api.post("/groups", payload);
                toast.success("Gruppo creato");
            }
            onSaved && onSaved();
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Errore salvataggio");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="group-edit-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">
                        {isEdit ? "Modifica gruppo" : "Nuovo gruppo"}
                    </DialogTitle>
                    <DialogDescription>
                        Dai un nome e un colore al gruppo. Potrai aggiungere dipendenti dopo la creazione.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label>Nome gruppo</Label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="es. Cantiere ASL Taranto"
                            className="mt-1.5"
                            data-testid="group-name-input"
                            autoFocus
                        />
                    </div>
                    <div>
                        <Label>Descrizione (opzionale)</Label>
                        <Textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="A cosa serve questo gruppo?"
                            rows={2}
                            className="mt-1.5"
                            data-testid="group-description-input"
                        />
                    </div>
                    <div>
                        <Label>Colore</Label>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {COLORS.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => setColor(c)}
                                    className={`h-7 w-7 rounded-full border-2 transition ${color === c ? "border-slate-800 ring-2 ring-offset-2 ring-slate-300" : "border-white shadow"}`}
                                    style={{ backgroundColor: c }}
                                    data-testid={`group-color-${c}`}
                                />
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label>Logo cliente (opzionale)</Label>
                        <p className="mt-1 text-xs text-slate-500">
                            Stampato automaticamente sul tesserino di <b>tutti i dipendenti del gruppo</b> (prevale sul logo personale).
                        </p>
                        <div className="mt-2 flex items-start gap-3">
                            <div className="flex h-16 w-28 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-dashed border-slate-300 bg-slate-50 p-1.5">
                                {clienteLogo ? (
                                    <img
                                        src={clienteLogo}
                                        alt="Logo cliente del gruppo"
                                        className="max-h-full max-w-full object-contain"
                                        data-testid="group-logo-preview"
                                    />
                                ) : (
                                    <span className="text-center text-[10px] uppercase tracking-wide text-slate-400">
                                        Nessun logo
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={logoInputRef}
                                    className="hidden"
                                    onChange={handleLogoFile}
                                    data-testid="group-logo-file-input"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => logoInputRef.current?.click()}
                                    className="gap-2"
                                    data-testid="group-logo-upload-btn"
                                >
                                    <ImageIcon className="h-3.5 w-3.5" />
                                    {clienteLogo ? "Sostituisci logo" : "Carica logo cliente"}
                                </Button>
                                {clienteLogo && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setClienteLogo(null)}
                                        className="gap-2 text-red-600 hover:bg-red-50 hover:text-red-700"
                                        data-testid="group-logo-remove-btn"
                                    >
                                        <X className="h-3.5 w-3.5" /> Rimuovi
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Annulla</Button>
                    <Button onClick={handleSave} disabled={saving} className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90" data-testid="group-save-btn">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        {isEdit ? "Salva" : "Crea gruppo"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const GroupDetailDialog = ({ open, groupId, initialGroup, onClose }) => {
    const [tab, setTab] = useState("members");
    const [group, setGroup] = useState(null);
    const [members, setMembers] = useState([]);
    const [allEmployees, setAllEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState(false);
    const [search, setSearch] = useState("");
    const [pickerOpen, setPickerOpen] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [printProgress, setPrintProgress] = useState({ current: 0, total: 0 });
    const [printSettings, setPrintSettings] = useState({ firma_datore: null, rotate_back: false });
    // Controlled badge preview: opens on hover (Radix HoverCard) AND on icon click/tap (touch devices)
    const [openPreviewId, setOpenPreviewId] = useState(null);
    const lastPreviewToggleRef = useRef(0);
    const [badgeFormat, setBadgeFormat] = useState("std"); // "std" 85,6×54 | "xl" 10×7 cm
    const printContainerRef = useRef(null);

    useEffect(() => {
        loadSettings().then((s) => {
            setPrintSettings({ firma_datore: s.firma_datore || null, rotate_back: !!s.rotate_back });
        }).catch(() => {});
    }, []);

    const fetchData = async () => {
        if (!groupId) return;
        setLoading(true);
        try {
            const [m, e] = await Promise.all([
                api.get(`/groups/${groupId}/members`),
                api.get("/employees"),
            ]);
            setGroup(m.data.group);
            setMembers(m.data.members);
            setAllEmployees(e.data);
        } catch (err) {
            toast.error("Errore caricamento");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && groupId) {
            setTab("members");
            setSearch("");
            // Seed from the parent's list so name/color/logo paint instantly,
            // then refresh with the authoritative data from the members call.
            setGroup(initialGroup || null);
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, groupId]);

    const memberIdsSet = useMemo(() => new Set(members.map((m) => m.id)), [members]);
    const nonMembers = useMemo(() => {
        const q = search.trim().toLowerCase();
        const matches = allEmployees
            // Only ACTIVE employees can be added to a group
            .filter((e) => !e.data_cessazione || !String(e.data_cessazione).trim())
            .filter((e) => !memberIdsSet.has(e.id))
            .filter((e) => !q ||
                e.cognome?.toLowerCase().includes(q) ||
                e.nome?.toLowerCase().includes(q) ||
                e.matricola?.toLowerCase().includes(q));
        return { items: matches.slice(0, 200), total: matches.length };
    }, [allEmployees, memberIdsSet, search]);

    const addMember = async (empId) => {
        setAdding(true);
        try {
            await api.post(`/groups/${groupId}/members`, { employee_ids: [empId] });
            await fetchData();
        } catch (e) {
            toast.error("Errore aggiunta");
        } finally {
            setAdding(false);
        }
    };

    const removeMember = async (empId) => {
        try {
            await api.delete(`/groups/${groupId}/members/${empId}`);
            await fetchData();
        } catch (e) {
            toast.error("Errore rimozione");
        }
    };

    // Build a fully-detailed list of group members from allEmployees (which include photo/logo)
    const detailedById = useMemo(() => {
        const m = new Map();
        allEmployees.forEach((e) => m.set(e.id, e));
        return m;
    }, [allEmployees]);

    const detailedMembers = useMemo(() => {
        const memberIds = new Set(members.map((m) => m.id));
        return allEmployees.filter((e) => memberIds.has(e.id));
    }, [members, allEmployees]);

    // Measure member photos asynchronously and flag those below 800×1000 px
    const [lowResIds, setLowResIds] = useState(() => new Set());
    useEffect(() => {
        let cancelled = false;
        if (detailedMembers.length === 0) {
            setLowResIds(new Set());
            return undefined;
        }
        Promise.all(detailedMembers.map(checkPhotoLowRes)).then((results) => {
            if (!cancelled) setLowResIds(new Set(results.filter(Boolean)));
        });
        return () => { cancelled = true; };
    }, [detailedMembers]);

    const handlePrintGroup = async () => {
        if (detailedMembers.length === 0) {
            toast.error("Il gruppo non ha dipendenti da stampare");
            return;
        }
        setPrinting(true);
        setPrintProgress({ current: 0, total: detailedMembers.length });
        // Give React a tick to render the off-screen badges into the print container
        await new Promise((r) => setTimeout(r, 200));
        try {
            const container = printContainerRef.current;
            if (!container) throw new Error("container missing");
            const items = Array.from(container.querySelectorAll("[data-print-pair]")).map((wrap) => ({
                front: wrap.querySelector("[data-print-front]"),
                back: wrap.querySelector("[data-print-back]"),
            }));
            if (items.length === 0) throw new Error("no badges rendered");
            const fname = `tesserini-gruppo-${(group?.name || "gruppo").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.pdf`;
            await downloadA4BadgesPDF(items, fname, {
                includeBack: true,
                rotateBack: printSettings.rotate_back,
                format: badgeFormat,
                onProgress: (p) => setPrintProgress(p),
            });
            recordPrintEvent("a4_pdf", detailedMembers.map((e) => e.id), items.length);
            toast.success(`Esportati ${items.length} tesserini del gruppo ${group?.name || ""}`);
        } catch (e) {
            toast.error("Errore durante l'esportazione PDF");
        } finally {
            setPrinting(false);
            setPrintProgress({ current: 0, total: 0 });
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="flex max-h-[90vh] flex-col max-w-3xl" data-testid="group-detail-dialog">
                <DialogHeader className="flex-shrink-0">
                    <DialogTitle className="font-display flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full" style={{ backgroundColor: group?.color || "#376DB3" }} />
                        {group?.name || "Gruppo"}
                    </DialogTitle>
                    <DialogDescription>
                        {members.length} dipendent{members.length === 1 ? "e" : "i"} nel gruppo
                    </DialogDescription>
                </DialogHeader>

                {/* Tabs */}
                <div className="flex flex-shrink-0 border-b border-slate-200">
                    <button
                        className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${tab === "members" ? "border-[#376DB3] text-[#376DB3]" : "border-transparent text-slate-500"}`}
                        onClick={() => setTab("members")}
                        data-testid="tab-members"
                    >
                        Dipendenti
                    </button>
                    <button
                        className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${tab === "comunicazione" ? "border-[#376DB3] text-[#376DB3]" : "border-transparent text-slate-500"}`}
                        onClick={() => setTab("comunicazione")}
                        data-testid="tab-comunicazione"
                    >
                        Invia comunicazione
                    </button>
                </div>

                <div className={`flex min-h-0 flex-1 flex-col overflow-hidden ${tab === "members" ? "" : "hidden"}`}>
                    <div className="my-3 flex flex-shrink-0 flex-wrap items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPickerOpen((v) => !v)}
                            className="gap-2"
                            data-testid="toggle-picker-btn"
                        >
                            <UserPlus className="h-4 w-4" /> {pickerOpen ? "Nascondi" : "Aggiungi dipendente"}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handlePrintGroup}
                            disabled={printing || detailedMembers.length === 0}
                            className="gap-2 border-[#376DB3] text-[#376DB3] hover:bg-[#376DB3]/5"
                            data-testid="print-group-btn"
                        >
                            {printing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                            {printing && printProgress.total > 0
                                ? `Genero ${printProgress.current}/${printProgress.total}…`
                                : `Stampa tesserini (${detailedMembers.length})`}
                        </Button>
                        <div
                            className="flex items-center gap-0.5 rounded-md border border-slate-200 bg-white p-0.5"
                            title="Formato dei tesserini"
                            data-testid="group-format-toggle-row"
                        >
                            <button
                                type="button"
                                onClick={() => setBadgeFormat("std")}
                                className={`rounded px-2 py-1 text-[11px] font-mono-numbers transition ${badgeFormat === "std" ? "bg-[#376DB3] text-white" : "text-slate-500 hover:bg-slate-100"}`}
                                data-testid="group-format-std"
                            >
                                85,6×54
                            </button>
                            <button
                                type="button"
                                onClick={() => setBadgeFormat("xl")}
                                className={`rounded px-2 py-1 text-[11px] font-mono-numbers transition ${badgeFormat === "xl" ? "bg-[#376DB3] text-white" : "text-slate-500 hover:bg-slate-100"}`}
                                data-testid="group-format-xl"
                            >
                                10×7 cm
                            </button>
                        </div>
                    </div>
                    {group?.cliente_logo && (
                        <div
                            className="mb-2 flex flex-shrink-0 items-center gap-2 rounded-md border border-blue-200 bg-blue-50/60 px-3 py-1.5 text-xs text-blue-900"
                            data-testid="group-logo-print-hint"
                        >
                            <img src={group.cliente_logo} alt="Logo cliente" className="h-5 max-w-[48px] object-contain" />
                            <span>La stampa di gruppo include il <b>logo cliente</b> su tutti i tesserini.</span>
                        </div>
                    )}
                    <div className={`mb-3 flex-shrink-0 rounded-md border border-slate-200 bg-slate-50 p-3 ${pickerOpen ? "" : "hidden"}`}>
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Cerca cognome, nome o matricola…"
                            className="bg-white"
                            data-testid="picker-search"
                        />
                        <div className={`mt-2 text-xs text-slate-500 ${nonMembers.total > nonMembers.items.length ? "" : "hidden"}`}>
                            Mostrati {nonMembers.items.length} di {nonMembers.total} dipendenti — affina la ricerca per restringere.
                        </div>
                        <div className="mt-2 max-h-72 space-y-1 overflow-y-auto">
                            {nonMembers.items.map((emp) => (
                                <button
                                    key={emp.id}
                                    onClick={() => addMember(emp.id)}
                                    disabled={adding}
                                    className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-white"
                                    data-testid={`picker-add-${emp.id}`}
                                >
                                    <span>
                                        <b>{emp.cognome} {emp.nome}</b> · matr. {emp.matricola}
                                    </span>
                                    <UserPlus className="h-3.5 w-3.5 text-emerald-600" />
                                </button>
                            ))}
                            <div className={`px-2 py-1.5 text-xs text-slate-500 ${nonMembers.items.length === 0 ? "" : "hidden"}`}>
                                {search ? "Nessun dipendente attivo trovato." : "Tutti i dipendenti attivi sono già nel gruppo."}
                            </div>
                        </div>
                    </div>
                    <div className={`flex-shrink-0 text-center text-slate-500 ${loading ? "" : "hidden"}`}>
                        <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                    </div>
                    <div className={`min-h-0 flex-1 overflow-y-auto ${!loading ? "" : "hidden"}`} data-testid="group-members-list">
                        <div className={`py-8 text-center text-sm text-slate-500 ${members.length === 0 ? "" : "hidden"}`}>
                            Nessun dipendente nel gruppo. Clicca "Aggiungi dipendente".
                        </div>
                        {members.map((m) => {
                            const det = detailedById.get(m.id) || m;
                            return (
                                <div key={m.id} className="flex items-center justify-between border-b border-slate-100 px-2 py-2 last:border-b-0">
                                    <HoverCard
                                        open={openPreviewId === m.id}
                                        onOpenChange={(o) => {
                                            // Ignore the hover-driven re-open that Radix fires right after
                                            // a click-to-close (pointer is still over the trigger).
                                            if (o && Date.now() - lastPreviewToggleRef.current < 400) return;
                                            setOpenPreviewId(o ? m.id : null);
                                        }}
                                        openDelay={100}
                                        closeDelay={100}
                                    >
                                        <div className="flex min-w-0 flex-1 items-center gap-1">
                                            <HoverCardTrigger asChild>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        lastPreviewToggleRef.current = Date.now();
                                                        setOpenPreviewId((v) => (v === m.id ? null : m.id));
                                                    }}
                                                    className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded text-[#376DB3] hover:bg-[#376DB3]/10"
                                                    title="Anteprima tesserino"
                                                    data-testid={`member-hover-${m.id}`}
                                                >
                                                    <IdCard className="h-4 w-4" />
                                                </button>
                                            </HoverCardTrigger>
                                            <Link
                                                to={`/dipendenti/${m.id}`}
                                                onMouseEnter={() => setOpenPreviewId(m.id)}
                                                onMouseLeave={() => setOpenPreviewId((v) => (v === m.id ? null : v))}
                                                className="min-w-0 flex-1 truncate text-sm hover:text-[#376DB3]"
                                                data-testid={`member-name-${m.id}`}
                                            >
                                                <b>{m.cognome} {m.nome}</b> · matr. {m.matricola}
                                            </Link>
                                            {lowResIds.has(m.id) && (
                                                <span
                                                    className="flex flex-shrink-0 items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200"
                                                    title="Foto sotto risoluzione consigliata (800×1000 px) — ricaricala per una stampa professionale"
                                                    data-testid={`member-lowres-${m.id}`}
                                                >
                                                    <AlertTriangle className="h-3 w-3" />
                                                    Foto bassa ris.
                                                </span>
                                            )}
                                        </div>
                                        <HoverCardContent
                                            side="right"
                                            align="start"
                                            className="w-auto p-2"
                                            data-testid={`member-badge-preview-${m.id}`}
                                        >
                                            {/* Same logo rule as the group print: group logo prevails */}
                                            <BadgePreview employee={{ ...det, cliente_logo: group?.cliente_logo || det.cliente_logo || null }} />
                                            <div className="mt-1.5 text-center text-[10px] text-slate-400">
                                                Anteprima tesserino{group?.cliente_logo ? " · logo cliente del gruppo" : ""}
                                            </div>
                                        </HoverCardContent>
                                    </HoverCard>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => removeMember(m.id)}
                                        className="text-slate-400 hover:text-red-600"
                                        data-testid={`remove-${m.id}`}
                                    >
                                        <UserMinus className="h-4 w-4" />
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className={`min-h-0 flex-1 overflow-y-auto ${tab === "comunicazione" ? "" : "hidden"}`}>
                    <ComunicazionePanel groupId={groupId} memberCount={members.length} />
                </div>

                <DialogFooter className="flex-shrink-0">
                    <Button variant="outline" onClick={onClose}>Chiudi</Button>
                </DialogFooter>
            </DialogContent>
            {/* Off-screen print container — only rendered while printing to avoid leaking heavy nodes */}
            <div className="print-only" aria-hidden ref={printContainerRef}>
                {printing && detailedMembers.map((emp) => (
                    <div key={emp.id} data-print-pair>
                        <div data-print-front>
                            {/* The group's client logo prevails over the personal one on group prints */}
                            <BadgePreview employee={{ ...emp, cliente_logo: group?.cliente_logo || emp.cliente_logo || null }} size={badgeFormat} />
                        </div>
                        <div data-print-back>
                            <BadgeBack
                                employee={emp}
                                firmaDatore={printSettings.firma_datore}
                                verifyBaseUrl={getVerifyBaseUrl()}
                                size={badgeFormat}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </Dialog>
    );
};

const ComunicazionePanel = ({ groupId, memberCount }) => {
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [file, setFile] = useState(null);
    const [sending, setSending] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const ref = useRef(null);

    const reset = () => {
        setTitle("");
        setMessage("");
        setFile(null);
        setLastResult(null);
        if (ref.current) ref.current.value = "";
    };

    const handleSend = async () => {
        if (!title.trim()) {
            toast.error("Inserisci il titolo");
            return;
        }
        if (!file && !message.trim()) {
            toast.error("Carica un PDF o scrivi un messaggio");
            return;
        }
        setSending(true);
        const fd = new FormData();
        fd.append("title", title);
        if (message) fd.append("message", message);
        if (file) fd.append("file", file);
        try {
            const { data } = await api.post(`/groups/${groupId}/comunicazione`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 300000,
            });
            setLastResult(data);
            toast.success(`Inviata a ${data.created} dipendenti`);
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Errore invio");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="my-4 space-y-4">
            <div className="rounded-md border border-blue-200 bg-blue-50/60 p-3 text-xs text-blue-900">
                La comunicazione sarà aggiunta come documento nella categoria <b>Comunicazione</b> nel portale di ciascuno dei <b>{memberCount} dipendenti attivi</b> del gruppo. I cessati non riceveranno la comunicazione.
            </div>

            <div>
                <Label>Titolo / oggetto</Label>
                <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="es. Circolare Natale 2026"
                    className="mt-1.5"
                    data-testid="comm-title"
                />
            </div>

            <div>
                <Label>Messaggio (opzionale, max ~1000 caratteri)</Label>
                <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value.slice(0, 1000))}
                    placeholder="Testo della comunicazione che verrà mostrato al dipendente…"
                    rows={4}
                    className="mt-1.5"
                    data-testid="comm-message"
                />
            </div>

            <div>
                <Label>Allegato PDF (opzionale)</Label>
                <div className="mt-1.5 rounded-md border border-dashed border-slate-300 bg-slate-50 p-3 text-center">
                    <div className="text-sm font-medium text-slate-700">
                        {file ? file.name : "Nessun file selezionato"}
                    </div>
                    <input
                        ref={ref}
                        type="file"
                        accept="application/pdf,image/*"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        className="hidden"
                        data-testid="comm-file-input"
                    />
                    <Button variant="outline" size="sm" className="mt-2 gap-2" onClick={() => ref.current?.click()}>
                        <Upload className="h-3.5 w-3.5" />
                        {file ? "Cambia file" : "Carica PDF"}
                    </Button>
                </div>
            </div>

            <div className={`rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 ${lastResult ? "" : "hidden"}`}>
                <MessageSquare className="mr-2 inline h-4 w-4" />
                Inviata a <b>{lastResult?.created || 0}</b> dipendenti del gruppo "{lastResult?.group?.name || ""}"
            </div>

            <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={reset} disabled={sending} data-testid="comm-reset">
                    Pulisci
                </Button>
                <Button
                    onClick={handleSend}
                    disabled={sending || !title.trim()}
                    className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                    data-testid="comm-send"
                >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Invia ai {memberCount} dipendenti
                </Button>
            </div>
        </div>
    );
};
